import {
  ApplicationConfig,
  inject,
  provideBrowserGlobalErrorListeners,
  provideEnvironmentInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { provideRouter, Routes, withComponentInputBinding } from '@angular/router';
import { ErrorPageComponent, LogoutPageComponent } from '@shared/ui-common';
import { AuthGuard, Roles } from '@shared/util-auth';
import { providePrimeNG } from 'primeng/config';
import { OAuthModuleConfig, provideOAuthClient } from 'angular-oauth2-oidc';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { ApiUrlInterceptor, WafErrorInterceptor } from '@shared/util-http';
import { ClientConfigStore, TranslationService } from '@shared/data';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import createOAuthConfig from '@shared/util-auth/oauth-config-factory';
import { DatePipe, DecimalPipe } from '@angular/common';
import { provideTranslateService } from '@ngx-translate/core';
import { appRoutingGuard } from './app.routing.guard';

export const LiOnLightPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#FEF2DF',
      100: '#FCDDB1',
      200: '#F9C77E',
      300: '#F7B04B',
      400: '#F69F24',
      500: '#F49000',
      600: '#F08200',
      700: '#EA7400',
      800: '#E46500',
      900: '#DA4A01',
      950: '#DA4A01'
    }
  },
  components: {
    button: {
      // label: {
      //   font: {
      //     weight: 'bold',
      //   },
      // },
      // secondary: {
      //   background: '#707172 !important',
      //   color: '#fff !important',
      //   hover: {
      //     background: '#606060 !important',
      //     color: '#fff !important',
      //   },
      // },
    },
    tag: {
      secondary: {
        background: '#0e3c8a !important',
        color: '#fff !important'
      }
    }
  }
});

const appRoutes: Routes = [
  {
    path: 'error',
    component: ErrorPageComponent
  },
  {
    path: 'logout',
    component: LogoutPageComponent
  },
  {
    path: 'information',
    loadChildren: () => import('@information/index').then((m) => m.InformationModule),
    canActivate: [AuthGuard],
    data: { roles: [Roles.PERMIT_ALL] }
  },
  {
    path: 'investment',
    loadChildren: () => import('@investment/index').then((m) => m.InvestmentModule),
    canActivate: [AuthGuard],
    data: { roles: [Roles.LIO_DZ_TRADER_1, Roles.LIO_VR_TRADER_1, Roles.LIO_FK_DZ_TRADER, Roles.LICO_TRADER] }
  },
  {
    path: 'investment100',
    loadChildren: () => import('@investment100/index').then((m) => m.Investment100Module),
    canActivate: [AuthGuard],
    data: { roles: [Roles.LIO_DZ_TRADER_1, Roles.LIO_VR_TRADER_1] }
  },
  {
    path: 'raising',
    loadChildren: () => import('@raising/index').then((m) => m.RaisingModule),
    canActivate: [AuthGuard],
    data: { roles: [Roles.LIO_DZ_TRADER_1, Roles.LIO_VR_TRADER_1] }
  },
  {
    path: 'refi',
    loadChildren: () => import('@refi/index').then((m) => m.RefiModule),
    canActivate: [AuthGuard],
    data: { roles: [Roles.LIO_DZ_TRADER_1, Roles.LIO_VR_REFI] }
  },
  {
    path: 'cancellation',
    loadChildren: () => import('@cancellation/index').then((m) => m.CancellationModule),
    canActivate: [AuthGuard],
    data: { roles: [Roles.LIO_DZ_TRADER_1, Roles.LIO_FK_DZ_TRADER] }
  },
  {
    path: 'trades-overview',
    loadChildren: () => import('@business-status/index').then((m) => m.BusinessStatusModule),
    canActivate: [AuthGuard],
    data: {
      roles: [
        Roles.LIO_DZ_READONLY,
        Roles.LIO_DZ_SYSADMIN,
        Roles.LIO_DZ_TRADER_1,
        Roles.LIO_VR_TRADER_1,
        Roles.LIO_VR_REFI,
        Roles.LIO_VR_READONLY,
        Roles.LIO_FK_DZ_TRADER,
        Roles.LICO_TRADER,
        Roles.LICO_READER
      ]
    }
  },
  {
    path: 'limit',
    loadChildren: () => import('@limit/index').then((m) => m.LimitModule),
    canActivate: [AuthGuard],
    data: { roles: [Roles.LIO_DZ_TRADER_1] }
  },
  {
    path: 'evaluation',
    loadChildren: () => import('@evaluation/index').then((m) => m.EvaluationModule),
    canActivate: [AuthGuard],
    data: {
      roles: [
        Roles.LIO_DZ_READONLY,
        Roles.LIO_DZ_SYSADMIN,
        Roles.LIO_DZ_TRADER_1,
        Roles.LIO_DZ_FUSION,
        Roles.LIO_VR_TRADER_1,
        Roles.LIO_VR_REFI,
        Roles.LIO_VR_READONLY,
        Roles.LIO_FK_DZ_TRADER,
        Roles.LICO_TRADER,
        Roles.LICO_READER
      ]
    }
  },
  {
    path: 'admin',
    loadChildren: () => import('@administration/index').then((m) => m.AdministrationModule),
    canActivate: [AuthGuard],
    data: {
      roles: [
        Roles.LIO_DZ_SYSADMIN,
        Roles.LIO_DZ_TRADER_1,
        Roles.LIO_FK_DZ_TRADER,
        Roles.LIO_DZ_READONLY,
        Roles.LIO_DZ_FUSION,
        Roles.LIO_FK_DZ_LIMIT_ADMIN,
        Roles.LIO_FK_DZ_LIMIT_READONLY
      ]
    }
  },
  {
    path: 'margin',
    loadChildren: () => import('@margin/index').then((m) => m.MarginModule),
    canActivate: [AuthGuard],
    data: { roles: [Roles.LIO_DZ_SYSADMIN, Roles.LIO_DZ_TRADER_1, Roles.LIO_FK_DZ_TRADER] }
  },
  {
    path: 'maturity',
    loadChildren: () => import('@maturity/index').then((m) => m.MaturityModule),
    canActivate: [AuthGuard],
    data: { roles: [Roles.LIO_DZ_SYSADMIN, Roles.LIO_DZ_TRADER_1, Roles.LIO_FK_DZ_TRADER, Roles.LIO_DZ_READONLY, Roles.LIO_FK_DZ_LIMIT_ADMIN] }
  },
  { path: '', children: [], canActivate: [appRoutingGuard] },
  { path: '**', redirectTo: 'information' }
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes, withComponentInputBinding()),

    // Order is important
    { provide: HTTP_INTERCEPTORS, useClass: WafErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiUrlInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi(), withFetch()),

    providePrimeNG({
      theme: {
        preset: LiOnLightPreset,
        options: {
          cssLayer: {
            name: 'primeng',
            order: 'primeng'
          },
          darkModeSelector: false
        }
      },
      ripple: true
    }),

    provideOAuthClient(),
    { provide: OAuthModuleConfig, useFactory: createOAuthConfig },

    provideEnvironmentInitializer(() => {
      const clientConfigStore = inject(ClientConfigStore);
      clientConfigStore.loadConfig();
      const translationService = inject(TranslationService);
      translationService.loadAndSetTranslationsAsync();
    }),

    provideTranslateService(),
    provideTranslateHttpLoader({
      prefix: '/i18n/',
      suffix: '.json'
    }),
    DatePipe,
    DecimalPipe
  ]
};
